import { reduce } from 'lodash'
import { store } from '../stores'
import { isFeatureOn } from '../VNConfigurations/Selectors'
import { VNCONFIGS_FEATURE_ENABLEMENT_KEYS } from '../VNConfigurations/Enums'

import { getDeviceId, getSessionId } from './deviceData'
import { stringToUUID } from './hashing'
import moment from 'moment'
import uuidv4 from 'uuid/v4'

export const orderUuidFromOrderNumber = (orderNumber) => {
  // The business date should be today in YYYY-MM-DD format.
  const businessDate = new Date().toISOString().split('T')[0]

  // Combine the business date and order number to create a unique order UUID.
  return stringToUUID(`${orderNumber}-${businessDate}-${process.env.REACT_APP_VENUE_UUID}-${getSessionId()}-${getDeviceId()}`)
}

export const currentOrderUuid = () => {
  return orderUuidFromOrderNumber(nextPosOrderNumber())
}

export const incrementNextOrderNumber = () => {
  const rangeMin = parseInt(localStorage.getItem('orderNumberMin')) || 1000
  const rangeMax = parseInt(localStorage.getItem('orderNumberMax')) || 1999

  let oldId = parseInt(localStorage.getItem('posOrderNumber')) || rangeMin

  // if it's out of range, let's assume the range has changed after a re-registration
  if (oldId < rangeMin || oldId > rangeMax) oldId = rangeMin

  // rollover / reset
  let newId = oldId + 1
  if (newId > rangeMax) newId = rangeMin

  localStorage.setItem('posOrderNumber', newId)
}

export const nextPosOrderNumber = () => {
  const rangeMin = parseInt(localStorage.getItem('orderNumberMin')) || 1000

  return `${parseInt(localStorage.getItem('posOrderNumber')) || rangeMin}`
}

const generateUuidWithForcedLengthAndNumberChars = (length, numberOfChars) => {
  const uuid = uuidv4().replace(/-/g, '') // strip dashes for compactness
  const shortenedUniqueIdentifier = uuid.slice(-length)

  const tagRegex = new RegExp(`(?=(?:.*\\d){${numberOfChars}})([a-z0-9]{${length}})$`, 'i')

  if (tagRegex.test(shortenedUniqueIdentifier)) {
    return shortenedUniqueIdentifier
  } else {
    return generateUuidWithForcedLengthAndNumberChars(length, numberOfChars)
  }
}

export const generateSplitOrderNumber = (orderNumber, tagLength = 3) => {
  const tagRegex = new RegExp(`(?=.*\\d)(-[a-z0-9]{${tagLength}}){1,}$`, 'i')
  const baseOrderNumber = `${orderNumber}`.replace(tagRegex, '')

  return `${baseOrderNumber}-${generateUuidWithForcedLengthAndNumberChars(tagLength, 2)}`
}

// Pad a number with leading zeros.
const padString = (str, length) => {
  let padded = str

  if (!str) {
    padded = ''
  }

  while (padded.length < length) {
    padded = `0${padded}`
  }

  return padded
}

// orderNumber : The order number as specified within the bounds of this device's order number
//     range.
// returns : An invoice number that can track the order number and device number within a payment
//     processor's system.

const hexKey = {
  a: '10',
  b: '11',
  c: '12',
  d: '13',
  e: '14',
  f: '15',
}

const convertToBaseTen = (deviceId) => {
  return reduce(deviceId?.toLowerCase().split(''), (convertedId, symbol) => convertedId + (hexKey[symbol] ?? symbol), '')
}

/**
 * Generates an invoice number based on the device ID and the order number.
 *
 * @deprecated scheduled for deprecation in v2.56 - use uniqueInvoiceNumber() instead
 */
export const legacyInvoiceNumber = (orderNumber) => {
  const curState = store.getState()
  const splitTabsEnabled = isFeatureOn(curState, VNCONFIGS_FEATURE_ENABLEMENT_KEYS.SPLIT_TABS)
  let parsedOrderNumber = orderNumber

  if (splitTabsEnabled) {
    parsedOrderNumber = orderNumber.replace(/[a-zA-Z-]/g, '')
  }

  const deviceId = convertToBaseTen(getDeviceId())
  return padString(deviceId.slice(-4), 4) + padString(parsedOrderNumber.slice(-6), 6)
}

/**
 * Takes the first three digits of the invoice number, increments it, and returns a new invoice number for refunding purposes.
 *
 * @link https://github.com/venuenext/stadium/blob/309fcf17d1d6666ad3b63f3ebac43ae3437b92b6/app/models/payment/handler/shift4.rb#L331C1-L335C6
 * @param {number} invoiceNumber
 * @returns
 */
export const incrementInvoiceNumber = (invoiceNumber, length = 10) => {
  if (!invoiceNumber) {
    return padString('', length)
  }

  if (typeof invoiceNumber === 'number') {
    invoiceNumber = invoiceNumber.toString()
  }

  const paddedInvoice = padString(invoiceNumber, length)
  const counter = parseInt(paddedInvoice.slice(0, 3), length) + 1
  const rest = paddedInvoice.slice(3)
  return padString(counter.toString().slice(-3), 3) + rest
}

const SLOT_DAY_THRESHOLD = 16
const reverseString = (str) => str.split('').reverse().join('')

/**
 * Generates the invoice number prefix based on parent CFD devicem, order number min or device ID
 *
 * @returns {String}
 */
export const invoiceNumberPrefix = () => {
  const cfdParentNumberPrefix = localStorage.getItem('invoiceNumberPrefix') || null
  if (cfdParentNumberPrefix) {
    return cfdParentNumberPrefix
  }

  const devicePrefix = convertToBaseTen(getDeviceId() || '0000').slice(-4)
  const orderNumberMin = Math.floor((parseInt(localStorage.getItem('orderNumberMin')) || null) / 1000)

  return padString((orderNumberMin || devicePrefix).toString(), 4)
}

/**
 * Generates a unique invoice number based on the device ID and the current timestamp
 *
 * @returns {string} The unique invoice number
 */
export const uniqueInvoiceNumber = () => {
  const prefix = invoiceNumberPrefix()
  const startOfDay = moment().startOf('day')
  const secondsSinceStartOfDay = moment().diff(startOfDay, 'seconds')
  const currentDay = moment().format('DD')
  let dayString = reverseString(`${currentDay}`)

  if (+currentDay > SLOT_DAY_THRESHOLD) {
    dayString = reverseString(`${+currentDay - SLOT_DAY_THRESHOLD}`)
  }

  const result = +dayString * 10_000 + secondsSinceStartOfDay
  if (result > 999_999) {
    return `${prefix}${result - 1_000_000}`
  }

  return `${prefix}${padString(result.toString(), 6)}`
}

/**
 *  Conditionally calls for either uniqueInvoiceNumber() or legacyInvoiceNumber() based on the 'UNIQUE_INVOICE_NUMBERS' feature flag.
 *
 * @param {String} orderNumber
 * @returns {String} The invoice number
 */
export const invoiceNumber = (orderNumber) => {
  const curState = store.getState()
  const useUniqueInvoiceNumbers = isFeatureOn(curState, VNCONFIGS_FEATURE_ENABLEMENT_KEYS.UNIQUE_INVOICE_NUMBERS)
  if (useUniqueInvoiceNumbers) {
    return uniqueInvoiceNumber()
  }
  return legacyInvoiceNumber(orderNumber)
}
